///
/// EarthBrowser
///
/// Created by Matt Giger on 10/25/2026
/// Copyright (c) 2026 Geodata Labs
///
/// Per-event products from the USGS detail feed (the `detail` URL stored on
/// every quake row; CORS-enabled, no key): the moment tensor's two nodal
/// planes, and the Operational Aftershock Forecast (OAF) with its
/// probability bins and ETAS/Omori parameters. Fetched on demand when a
/// cutout opens, cached for the session.
///

const cCache = new Map<string, Promise<UsgsEventDetail | undefined>>()
const cFetchTimeoutMs = 12000

export interface NodalPlane {
	strikeDeg: number
	dipDeg: number
	rakeDeg: number
}

export interface MomentTensor {
	source: string
	np1: NodalPlane
	np2: NodalPlane
	percentDoubleCouple?: number
	derivedDepthKm?: number
}

export interface ForecastBin {
	magnitude: number
	probability: number
	/** Expected count (median) when published */
	median?: number
}

export interface ForecastWindow {
	label: string
	timeStartMs: number
	timeEndMs: number
	bins: ForecastBin[]
}

export interface AftershockForecast {
	modelName: string
	/** Omori p and c, Gutenberg-Richter b and completeness Mc from the model fit */
	p?: number
	c?: number
	b?: number
	mc?: number
	creationTimeMs: number
	expireTimeMs: number
	reviewed: boolean
	windows: ForecastWindow[]
}

export interface UsgsEventDetail {
	momentTensor?: MomentTensor
	forecast?: AftershockForecast
}

type Product = {
	properties?: Record<string, string>
	contents?: Record<string, { url?: string }>
}

type DetailFeature = {
	properties?: {
		products?: Record<string, Product[]>
	}
}

type OafJson = {
	creationTime?: number
	expireTime?: number
	model?: { name?: string; parameters?: Record<string, number | string> }
	forecast?: Array<{
		timeStart: number
		timeEnd: number
		label: string
		bins: Array<{ magnitude: number; probability: number; median?: number }>
	}>
}

export class UsgsDetail {
	/** Detail products for a quake; undefined when unavailable or offline */
	public static load(detailUrl: string | undefined): Promise<UsgsEventDetail | undefined> {
		if(!detailUrl?.startsWith("https://earthquake.usgs.gov/")) {
			return Promise.resolve(undefined)
		}
		let pending = cCache.get(detailUrl)
		if(!pending) {
			pending = this.fetchDetail(detailUrl).catch(() => undefined)
			cCache.set(detailUrl, pending)
		}
		return pending
	}

	private static async fetchDetail(url: string): Promise<UsgsEventDetail | undefined> {
		const feature = await fetchJson<DetailFeature>(url)
		const products = feature?.properties?.products
		if(!products) return undefined

		const detail: UsgsEventDetail = {}

		const mt = products["moment-tensor"]?.[0] ?? products["focal-mechanism"]?.[0]
		const mp = mt?.properties
		if(mp) {
			const num = (key: string): number | undefined => {
				const v = Number(mp[key])
				return Number.isFinite(v) ? v : undefined
			}
			const np1 = plane(num("nodal-plane-1-strike"), num("nodal-plane-1-dip"), num("nodal-plane-1-rake"))
			const np2 = plane(num("nodal-plane-2-strike"), num("nodal-plane-2-dip"), num("nodal-plane-2-rake"))
			if(np1 && np2) {
				detail.momentTensor = {
					source: mp["beachball-source"] ?? mp.eventsource ?? "",
					np1,
					np2,
					percentDoubleCouple: num("percent-double-couple"),
					derivedDepthKm: num("derived-depth")
				}
			}
		}

		const oaf = products.oaf?.[0]
		const forecastUrl = oaf?.contents
			? Object.entries(oaf.contents).find(([name]) => name.endsWith(".json"))?.[1]?.url
			: undefined
		if(forecastUrl) {
			const json = await fetchJson<OafJson>(forecastUrl)
			if(json?.forecast) {
				const params = json.model?.parameters ?? {}
				const pnum = (key: string): number | undefined => {
					const v = Number(params[key])
					return Number.isFinite(v) ? v : undefined
				}
				detail.forecast = {
					modelName: json.model?.name ?? "USGS aftershock forecast",
					p: pnum("p"),
					c: pnum("c"),
					b: pnum("b"),
					mc: pnum("Mc"),
					creationTimeMs: json.creationTime ?? 0,
					expireTimeMs: json.expireTime ?? 0,
					reviewed: oaf?.properties?.["review-status"] === "reviewed",
					windows: json.forecast.map(w => ({
						label: w.label,
						timeStartMs: w.timeStart,
						timeEndMs: w.timeEnd,
						bins: w.bins.map(b => ({ magnitude: b.magnitude, probability: b.probability, median: b.median }))
					}))
				}
			}
		}

		return detail.momentTensor || detail.forecast ? detail : undefined
	}
}

function plane(strike?: number, dip?: number, rake?: number): NodalPlane | undefined {
	if(strike === undefined || dip === undefined) return undefined
	return { strikeDeg: strike, dipDeg: dip, rakeDeg: rake ?? 0 }
}

async function fetchJson<T>(url: string): Promise<T | undefined> {
	const controller = new AbortController()
	const timer = setTimeout(() => controller.abort(), cFetchTimeoutMs)
	try {
		const res = await fetch(url, { signal: controller.signal })
		if(!res.ok) return undefined
		return await res.json() as T
	} finally {
		clearTimeout(timer)
	}
}
